import { useCallback, useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "@/hooks/useDebounce";
import {
  lookupCompanyName,
  normalizePNumbers,
  type CompanyNameLookupItem,
  type CompanyNameLookupResult,
} from "@blanket/services/blanketApi";
import { createRegistrationRowId } from "@blanket/pages/registration/model/registrationInitialValues";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 400;

interface FieldMapping {
  name: string;
  cvr: string;
  pNumber?: string;
  pNumbers?: string;
}

export const useCompanyNameLookup = (fields: FieldMapping) => {
  const { getFieldMeta, setFieldValue } = useFormikContext();
  const rawValue = (getFieldMeta(fields.name).value as string) ?? "";
  const trimmedRaw = rawValue.trim();
  const debouncedName = useDebounce(trimmedRaw, DEBOUNCE_MS);

  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [availablePNumbers, setAvailablePNumbers] = useState<number[]>([]);

  // TODO(react-hooks-7): "latest ref" pattern — these writes happen during
  // render, which react-hooks/refs flags. Moving them into an effect would
  // change when the refs become current relative to the effects below, so the
  // pattern is kept for now and revisited with the set-state-in-effect
  // refactor further down this file.
  const fieldsRef = useRef(fields);
  // eslint-disable-next-line react-hooks/refs
  fieldsRef.current = fields;
  const setFieldValueRef = useRef(setFieldValue);
  // eslint-disable-next-line react-hooks/refs
  setFieldValueRef.current = setFieldValue;

  const isEditingSearch =
    trimmedRaw.length >= MIN_QUERY_LENGTH &&
    (selectedName === null || trimmedRaw !== selectedName);

  // Re-open suggestions when the user edits away from a prior selection (not on debounce lag).
  // TODO(react-hooks-7): derive `isDismissed` from the edit state instead of
  // resetting it in an effect.
  useEffect(() => {
    if (isEditingSearch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsDismissed(false);
    }
  }, [isEditingSearch]);

  // Clear linked fields only when the visible input diverges from the selection.
  // TODO(react-hooks-7): this effect both resets local state and pushes empty
  // values into Formik; splitting the Formik write from the state reset is the
  // real fix.
  useEffect(() => {
    if (selectedName !== null && trimmedRaw !== selectedName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedName(null);
      setAvailablePNumbers([]);
      const f = fieldsRef.current;
      const sfv = setFieldValueRef.current;
      if (f.pNumber) sfv(f.pNumber, "");
      sfv(f.cvr, "");
    }
  }, [trimmedRaw, selectedName]);

  const showSuggestions = isEditingSearch && !isDismissed;

  const query = useQuery<CompanyNameLookupResult>({
    queryKey: ["companyNameLookup", fields.name, debouncedName],
    queryFn: ({ signal }) => lookupCompanyName(debouncedName, signal),
    enabled: isEditingSearch && debouncedName.length >= MIN_QUERY_LENGTH,
    staleTime: 1000 * 60 * 5,
  });

  const select = useCallback(
    (item: CompanyNameLookupItem) => {
      const trimmed = (item.companyName ?? "").trim();
      const pNums = normalizePNumbers(item.pNumbers);

      setSelectedName(trimmed);
      setIsDismissed(true);
      setFieldValue(fields.name, trimmed);
      setFieldValue(fields.cvr, String(item.cvr));

      if (fields.pNumber) {
        setAvailablePNumbers(pNums);
        setFieldValue(
          fields.pNumber,
          pNums.length === 1 ? String(pNums[0]) : "",
        );
      }

      if (fields.pNumbers && pNums.length) {
        const mapped = pNums.map((p) => ({
          _rowId: createRegistrationRowId(),
          value: String(p),
        }));
        setFieldValue(
          fields.pNumbers,
          mapped.length
            ? mapped
            : [{ _rowId: createRegistrationRowId(), value: "" }],
        );
      }
    },
    [fields, setFieldValue],
  );

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  return {
    availablePNumbers,
    data: showSuggestions ? query.data : undefined,
    isLoading: query.isFetching && showSuggestions,
    error: query.error,
    select,
    dismiss,
  };
};
